import Header from '../layout/Header';
import Footer from '../layout/Footer';
import WhyPerfectSection from '../landing/WhyPerfectSection';
import {ALL_REVIEWS} from '../../config/reviews';
import {blogArticles, getBlogArticleBySlug} from '../../config/blogArticles';

const VIDEO_WHY_PERFECT = {
    title: 'Видеопоздравление от ИИ-звезды',
    titleHighlight: 'идеально подходит,',
    reasons: [
        'Хочешь оригинально поздравить, но не знаешь как?',
        'Живые звезды стоят очень дорого и не отвечают в ЛС?',
        'У тебя мало времени, нужно срочно поздравить и произвести вау-эффект?'
    ]
};

const MUSIC_WHY_PERFECT = {
    title: 'Персональная песня от ИИ',
    titleHighlight: 'идеально подходит,',
    reasons: [
        'Хочешь подарить уникальную песню, но не умеешь писать музыку?',
        'Профессиональные музыканты стоят дорого и делают долго?',
        'Нужен быстрый и запоминающийся подарок с вау-эффектом?'
    ]
};

function splitAfterSecondParagraph(html) {
    const first = html.indexOf('</p>');
    if (first === -1) return [html, ''];
    const second = html.indexOf('</p>', first + 1);
    if (second === -1) return [html, ''];
    const pos = second + '</p>'.length;
    return [html.slice(0, pos), html.slice(pos)];
}

function splitContentInMiddle(html) {
    const tags = ['</h2>', '</p>', '</ul>'];
    const mid = Math.floor(html.length / 2);
    let bestPos = -1;
    let bestDist = Infinity;
    for (const tag of tags) {
        let idx = html.indexOf(tag, mid - 200);
        while (idx !== -1 && idx < mid + 200) {
            const pos = idx + tag.length;
            const dist = Math.abs(pos - mid);
            if (dist < bestDist) {
                bestDist = dist;
                bestPos = pos;
            }
            idx = html.indexOf(tag, idx + 1);
        }
    }
    if (bestPos === -1) return [html, ''];
    return [html.slice(0, bestPos), html.slice(bestPos)];
}

function formatDate(dateStr) {
    const months = [
        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
    ];
    const d = new Date(dateStr);
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function BlogArticlePage({slug}) {
    const article = getBlogArticleBySlug(slug);

    if (!article) {
        window.location.href = '/blog/';
        return null;
    }

    const articleIndex = blogArticles.findIndex(a => a.slug === slug);
    const review = ALL_REVIEWS[articleIndex % ALL_REVIEWS.length];
    const otherArticles = blogArticles.filter(a => a.slug !== slug).slice(0, 3);
    const [firstParagraph, restContent] = splitAfterSecondParagraph(article.content);
    const [contentTop, contentBottom] = splitContentInMiddle(restContent);
    const isMusic = article.type === 'music';
    const ctaHref = isMusic ? '/music' : '/';
    const ctaText = isMusic ? 'Создать песню' : 'Создать поздравление';

    const proseClasses = `prose prose-lg max-w-none
        [&>p]:text-brand-dark/80 [&>p]:leading-relaxed [&>p]:mb-5
        [&>h2]:text-xl [&>h2]:md:text-2xl [&>h2]:font-bold [&>h2]:text-brand-dark [&>h2]:mt-8 [&>h2]:mb-4
        [&>h3]:text-lg [&>h3]:md:text-xl [&>h3]:font-bold [&>h3]:text-brand-dark [&>h3]:mt-6 [&>h3]:mb-3
        [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-5 [&>ul>li]:text-brand-dark/80 [&>ul>li]:mb-2 [&>ul>li]:leading-relaxed`;

    return (
        <div className="min-h-screen bg-white font-sans flex flex-col">
            <Header onHomeClick={() => window.location.href = '/'}/>

            <main className="flex-1 w-full max-w-[800px] mx-auto px-4 py-8">
                <nav className="text-sm text-brand-dark/50 mb-6">
                    <a href="/" className="hover:text-brand-red transition">Главная</a>
                    <span className="mx-2">/</span>
                    <a href="/blog/" className="hover:text-brand-red transition">Блог</a>
                    <span className="mx-2">/</span>
                    <span className="text-brand-dark">{article.title}</span>
                </nav>

                <article>
                    <time className="text-sm text-brand-dark/40">{formatDate(article.date)}</time>
                    <h1 className="text-3xl md:text-4xl font-bold text-brand-dark mt-2 mb-8">
                        {article.title}
                    </h1>

                    <div className={proseClasses} dangerouslySetInnerHTML={{__html: firstParagraph}}/>

                    <a
                        href={ctaHref}
                        className="block w-full my-6 px-6 py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-center text-sm font-medium tracking-[-0.14px] hover:opacity-95 transition">
                        {ctaText}
                    </a>

                    <div className={proseClasses} dangerouslySetInnerHTML={{__html: contentTop}}/>
                </article>

                <WhyPerfectSection config={{
                    ...(isMusic ? MUSIC_WHY_PERFECT : VIDEO_WHY_PERFECT),
                    blogCta: true,
                    ctaHref,
                    ctaText,
                }}/>

                <article>
                    <div className={proseClasses} dangerouslySetInnerHTML={{__html: contentBottom}}/>
                </article>

                <section className="mt-12 pt-8 border-t border-brand-dark/10">
                    <h2 className="text-2xl font-bold text-brand-dark mb-6">Отзыв от звезды</h2>
                    <div className="flex gap-4 items-start rounded-2xl border border-brand-dark/10 p-4">
                        <img src={review.image} alt={review.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                        <div>
                            <p className="font-bold text-brand-dark text-sm">{review.name}</p>
                            <p className="text-brand-dark/70 text-sm leading-relaxed mt-1">{review.text}</p>
                        </div>
                    </div>
                </section>

                {otherArticles.length > 0 && (
                    <section className="mt-12 pt-8 border-t border-brand-dark/10">
                        <h2 className="text-2xl font-bold text-brand-dark mb-6">Читайте также</h2>
                        <div className="flex flex-col gap-4">
                            {otherArticles.map(a => (
                                <a
                                    key={a.slug}
                                    href={`/blog/${a.slug}/`}
                                    className="block group rounded-xl border border-brand-dark/10 p-4 hover:border-brand-red/30 transition"
                                >
                                    <h3 className="font-bold text-brand-dark group-hover:text-brand-red transition">
                                        {a.title}
                                    </h3>
                                    <p className="text-sm text-brand-dark/60 mt-1">{a.excerpt}</p>
                                </a>
                            ))}
                        </div>
                    </section>
                )}

                <div className="mt-8">
                    <a href="/blog/"
                       className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium hover:opacity-95 transition">
                        ← Все статьи
                    </a>
                </div>
            </main>

            <Footer
                onMyVideosClick={() => {
                }}
                onGenerateClick={() => window.location.href = '/'}
            />
        </div>
    );
}
